import type {Store} from 'jotai'

import {countryAtom} from '@/lib/locale/country'

import {type FoodSupportedCountries, isFoodSupportedCountry, TIPS_PRESET} from './config'
import type {TokenProps, UserLocation} from './type'

export function getUserLocation(): UserLocation {
  return {
    deliveryType: 'delivery', // default
    userAddress: {
      latitude: 0,
      longitude: 0,
      address: '',
      address_detail: {
        street_number: '',
        route: '',
        locality: '',
        administrative_area_level_1: '',
        country: '',
        postal_code: '',
      },
    },
  }
}

export function getTokenInfo(): TokenProps {
  return {
    tokenInfo: {
      token: '',
      token_expire_time: 0,
    },
  }
}

const userLocationStorage = createJSONStorage<UserLocation>(() => localStorage)

const tokenInfoStorage = createJSONStorage<TokenProps>(() => localStorage)

export const userLocationInfoAtom = atomWithStorage<UserLocation>(
  'grubhubDeliveryInformation',
  getUserLocation(),
  userLocationStorage,
  {
    getOnInit: true,
  },
)

export const tokenInfoAtom = atomWithStorage<TokenProps>(
  'tokenInformation',
  getTokenInfo(),
  tokenInfoStorage,
  {
    getOnInit: true,
  },
)

const deliveryTypeAtom = focusAtom(userLocationInfoAtom, optic => optic.prop('deliveryType'))
const addressAtom = focusAtom(userLocationInfoAtom, optic => optic.prop('userAddress'))
const tokenInfo = focusAtom(tokenInfoAtom, optic => optic.prop('tokenInfo'))

export function useGrubhubDeliveryType() {
  return useAtom(deliveryTypeAtom)
}

export function useGrubhubUserAddress() {
  return useAtom(addressAtom)
}

export function useSetGrubhubUserAddress() {
  return useSetAtom(addressAtom)
}

export function useGetGrubhubUserAddress(store: Store) {
  return useCallback(() => store.get(addressAtom), [store])
}

export function useGrubhubTokenInfo() {
  return useAtom(tokenInfo)
}

const dropOffNoteStorage = createJSONStorage<string>(() => localStorage)

export const dropOffNoteAtom = atomWithStorage<string>(
  'grubhubDropOffNote',
  '',
  dropOffNoteStorage,
  {
    getOnInit: true,
  },
)

export function useDropOffNote() {
  return useAtom(dropOffNoteAtom)
}

export function useSetDropOffNote() {
  return useSetAtom(dropOffNoteAtom)
}

export function useGetDropOffNote() {
  const store = useStore()
  return useCallback(() => store.get(dropOffNoteAtom), [store])
}

export interface TipsData {
  type: 'preset' | 'custom'
  data: number
}

const tipsStorage = createJSONStorage<TipsData>(() => localStorage)

export const tipsAtom = atomWithStorage(
  'grubhubTips',
  {
    type: 'preset',
    data: 0,
  } as TipsData,
  tipsStorage,
  {
    getOnInit: true,
  },
)

export function useTips() {
  return useAtom(tipsAtom)
}

export function useSetTips() {
  return useSetAtom(tipsAtom)
}

export function useGetTips() {
  const store = useStore()
  return useCallback(() => store.get(tipsAtom), [store])
}

export function getTipsAmount(tips: TipsData, country: FoodSupportedCountries) {
  if (tips.type === 'custom') return tips.data
  return TIPS_PRESET[country][tips.data] ?? 0
}

export function useGetTipsAmount() {
  const store = useStore()
  return useCallback(() => {
    const tips = store.get(tipsAtom)
    const country = store.get(countryAtom)
    invariant(isFoodSupportedCountry(country), 'Country is not supported')
    return getTipsAmount(tips, country)
  }, [store])
}
